import { useState, useCallback } from 'react';
import { rgbToHex } from '../utils';

interface UseEyeDropperReturn {
  isSupported: boolean;
  isActive: boolean;
  pickColor: () => Promise<string | null>;
}

/**
 * Hook para usar la API EyeDropper del navegador
 * Permite seleccionar un color de cualquier parte de la pantalla
 */
export const useEyeDropper = (): UseEyeDropperReturn => {
  const [isActive, setIsActive] = useState(false);

  // Verificar si la API EyeDropper está disponible
  const isSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

  const pickColor = useCallback(async (): Promise<string | null> => {
    if (!isSupported) {
      // Fallback: usar método alternativo con canvas
      return await pickColorWithCanvas();
    }

    try {
      setIsActive(true);
      const eyeDropper = new (
        window as unknown as Window & {
          EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> };
        }
      ).EyeDropper();
      const result = await eyeDropper.open();

      if (result && result.sRGBHex) {
        // La API devuelve el color en formato sRGBHex (ej: "#ff0000")
        return result.sRGBHex.toUpperCase();
      }

      return null;
    } catch (error: unknown) {
      // El usuario canceló la selección o hubo un error
      if (error instanceof Error && error.name !== 'AbortError') {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Error al usar EyeDropper:', error);
        }
      }
      return null;
    } finally {
      setIsActive(false);
    }
  }, [isSupported]);

  return {
    isSupported,
    isActive,
    pickColor,
  };
};

/**
 * Método alternativo usando canvas para capturar el color del pixel
 * Funciona en navegadores que no soportan la API EyeDropper
 */
async function pickColorWithCanvas(): Promise<string | null> {
  return new Promise((resolve) => {
    // Crear un overlay que capture los clicks
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999999;
      cursor: crosshair;
      background: transparent;
    `;

    const cleanup = () => {
      document.body.removeChild(overlay);
      document.body.style.cursor = '';
    };

    const handleClick = async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        // Capturar la pantalla usando html2canvas o similar
        // Por simplicidad, intentamos obtener el color del elemento clickeado
        const element = document.elementFromPoint(e.clientX, e.clientY);
        if (element) {
          const style = window.getComputedStyle(element);
          const bgColor = style.backgroundColor;

          if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
            // Convertir rgb/rgba a hex
            const rgbMatch = bgColor.match(/\d+/g);
            if (rgbMatch && rgbMatch.length >= 3) {
              const r = parseInt(rgbMatch[0]);
              const g = parseInt(rgbMatch[1]);
              const b = parseInt(rgbMatch[2]);
              const hex = rgbToHex(r, g, b);
              cleanup();
              resolve(hex);
              return;
            }
          }
        }

        // Si no se puede obtener del elemento, intentar con canvas
        // Nota: Esto requiere permisos adicionales y puede no funcionar en todos los navegadores
        cleanup();
        resolve(null);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Error al capturar color:', error);
        }
        cleanup();
        resolve(null);
      }
    };

    overlay.addEventListener('click', handleClick);
    overlay.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      cleanup();
      resolve(null);
    });

    // Cancelar con Escape
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup();
        document.removeEventListener('keydown', handleEscape);
        resolve(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.appendChild(overlay);
    document.body.style.cursor = 'crosshair';
  });
}
